import { gql, useQuery } from '@apollo/client';
import {
  TRANSLATED_STRING_FIELDS,
  translateBackend,
} from '@assets/i18n/config';
import type { User } from '@interfaces/user';
import { EntityTypes } from '@interfaces/user';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { Selector } from '@ovComponents/2-component/selector/selector';
import {
  Table,
  TableBody,
  TableCell,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { UserContext } from '@providers/userContextProvider';
import { generateClientNameString } from '@utils/generateClientNameString';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box, Button, Skeleton, Typography } from 'hephaestus/web';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const FETCH_USERS = gql`
  query searchUsers($input: UserSearchInput!, $currency: StatisticsCurrencyTypes) {
    searchUsers(input: $input) {
      users {
        id
        firstName
        lastName
        entityName
        primaryEmail
        statistics(input: { currency: $currency }) { marketValueCents }
        accounts {
          id
        }
        organization { translatedName { ${TRANSLATED_STRING_FIELDS} } }
      }
      totalCount
    }
  }
`;

const RIGHT_ALIGNED_COLS = ['totalAssets', 'organization', 'numberOfAccounts'];

export const Report = ({ options = {} }: { options?: any }) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['clients', 'homePageConfiguration']);
  const { activeOrganization, activeCurrency } = useContext(UserContext);
  const [filter, setFilter] = useState<'ANY' | 'INDIVIDUAL' | 'NON_INDIVIDUAL'>(
    'ANY',
  );
  const tableOptions = (options.clientTable ?? []).filter(Boolean);

  const filterMap: Record<string, string[] | undefined> = {
    ANY: undefined,
    INDIVIDUAL: [EntityTypes.INDIVIDUAL],
    NON_INDIVIDUAL: [
      EntityTypes.PUBLICLY_LISTED_ENTITY,
      EntityTypes.PRIVATE_COMPANY,
      EntityTypes.PARTNERSHIP,
      EntityTypes.SOLE_PROPRIETORSHIP,
      EntityTypes.TRUST,
      EntityTypes.INVESTMENT_FUND,
      EntityTypes.REGULATED_ENTITY,
      EntityTypes.OTHER,
    ],
  };

  const sortMap: Record<string, string> = {
    fullName: 'firstName',
    primaryEmail: 'email',
    totalAssets: 'marketValueCents',
  };
  const { loading, data, previousData } = useQuery(FETCH_USERS, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
          types: filterMap[filter],
        },
        pagination: {
          sortField: sortMap[options.clientSortBy],
          sortDesc: options.clientSortDirection === 'desc',
          perPage: 5,
        },
      },
      currency: activeCurrency || 'CAD',
    },
  });

  return (
    <Card loading={loading}>
      <Box>
        <CardContent
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant='titleLarge'>
            {translateBackend(options.customTitle)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Selector
              sx={{ marginLeft: 3 }}
              onChange={(e: any) =>
                setFilter(
                  e.target.value as 'ANY' | 'INDIVIDUAL' | 'NON_INDIVIDUAL',
                )
              }
              selectedOptionType={filter}
              options={[
                {
                  label: t('homePageConfiguration:report.filter.any'),
                  type: 'ANY',
                },
                {
                  label: t('homePageConfiguration:report.filter.individual'),
                  type: 'INDIVIDUAL',
                },
                {
                  label: t('homePageConfiguration:report.filter.nonIndividual'),
                  type: 'NON_INDIVIDUAL',
                },
              ]}
            />
            <Button
              variant='text'
              onClick={() => {
                if (filter === 'NON_INDIVIDUAL') {
                  navigate('/nonIndividualClients');
                } else {
                  navigate('/clients');
                }
              }}
            >
              {t('shared:viewAll')}
            </Button>
          </Box>
        </CardContent>
        <Box sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 650 }} aria-label='table'>
            <TableBody>
              <TableRow>
                {tableOptions.map((opt: any) => {
                  const right = RIGHT_ALIGNED_COLS.includes(opt.type);

                  return (
                    <TableHeadCell right={right}>
                      {translateBackend(opt.label)}
                    </TableHeadCell>
                  );
                })}
              </TableRow>

              {loading &&
                !previousData &&
                [...Array(5)].map((_x: any, i: number) => (
                  <TableRow key={i}>
                    {tableOptions.map(() => (
                      <TableCell>
                        <Skeleton width='100%' />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {(data || previousData)?.searchUsers?.users?.map((user: User) => (
                <TableRow hover key={user.id} href={`/clients/${user.id}`}>
                  {tableOptions.map((opt: any) => (
                    <UserCell user={user} type={opt.type} />
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Card>
  );
};

const UserCell = ({ user, type }: { user: User; type: string }) => {
  switch (type) {
    case 'fullName':
      return <TableCell>{generateClientNameString(user)}</TableCell>;
    case 'primaryEmail':
      return <TableCell>{user.primaryEmail}</TableCell>;
    case 'totalAssets':
      return (
        <TableCell right>
          {formatMoneyValue(user.statistics?.marketValueCents)}
        </TableCell>
      );
    case 'numberOfAccounts':
      return <TableCell right>{user.accounts?.length ?? 0}</TableCell>;
    case 'organization':
      return (
        <TableCell right>
          {translateBackend(user.organization?.translatedName)}
        </TableCell>
      );
    default:
      return <TableCell />;
  }
};
